import React, { useState, useEffect } from 'react';
import { chapters, levels, questions, achievements } from './data/questions';
import { UserProgress, Level, User, Question } from './types';
import { 
  loadFromLocalStorage, 
  saveToLocalStorage, 
  generateUserId,
  getToday
} from './utils';
import HomePage from './pages/HomePage';
import ChallengePage from './pages/ChallengePage';
import QuizPage from './pages/QuizPage';
import ProfilePage from './pages/ProfilePage';
import ResultPage from './pages/ResultPage';

type Page = 'home' | 'challenge' | 'quiz' | 'profile' | 'result';

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [user, setUser] = useState<User | null>(null);
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [currentLevel, setCurrentLevel] = useState<Level | null>(null);
  const [currentQuestions, setCurrentQuestions] = useState<Question[]>([]);
  const [quizResult, setQuizResult] = useState<any>(null);

  useEffect(() => {
    initializeUser();
  }, []);

  const initializeUser = () => {
    let storedUser = loadFromLocalStorage<User | null>('chemistry_user', null);
    let storedProgress = loadFromLocalStorage<UserProgress | null>('chemistry_progress', null);

    if (!storedUser) {
      storedUser = {
        id: generateUserId(),
        nickname: '化学学习者',
        avatar: '👨‍🎓',
        school: '海淀区中学',
        grade: '高二',
        class: '1班',
        joinDate: getToday()
      };
      saveToLocalStorage('chemistry_user', storedUser);
    }

    if (!storedProgress) {
      storedProgress = {
        userId: storedUser.id,
        currentLevel: 1,
        totalScore: 0,
        completedLevels: [],
        achievements: [],
        dailyChallenges: 3,
        lastChallengeDate: '',
        streak: 0,
        weeklyScore: 0,
        monthlyScore: 0
      };
      saveToLocalStorage('chemistry_progress', storedProgress);
    }

    if (storedProgress.lastChallengeDate !== getToday()) {
      storedProgress.dailyChallenges = 3;
      storedProgress.lastChallengeDate = getToday();
      saveToLocalStorage('chemistry_progress', storedProgress);
    }

    setUser(storedUser);
    setProgress(storedProgress);
  };

  const handleSelectLevel = (level: Level) => {
    // 移除挑战次数限制，允许无限次答题
    // if (!progress || progress.dailyChallenges <= 0) {
    //   alert('今日挑战次数已用完，明天再来吧！');
    //   return;
    // }

    const levelQuestions = questions.filter(q => 
      level.questions.includes(q.id)
    ).slice(0, 10);

    setCurrentLevel(level);
    setCurrentQuestions(levelQuestions);
    setCurrentPage('quiz');
  };

  const handleQuizComplete = (result: any) => {
    setQuizResult(result);
    
    if (progress && currentLevel) {
      const updatedProgress = { ...progress };
      
      updatedProgress.totalScore += result.score;
      updatedProgress.weeklyScore += result.score;
      updatedProgress.monthlyScore += result.score;
      // 不移除挑战次数，允许无限次答题
      // updatedProgress.dailyChallenges -= 1;
      
      if (result.passed && !updatedProgress.completedLevels.includes(currentLevel.id)) {
        updatedProgress.completedLevels.push(currentLevel.id);
      }

      saveToLocalStorage('chemistry_progress', updatedProgress);
      setProgress(updatedProgress);
    }

    setCurrentPage('result');
  };

  const handleRestart = () => {
    setCurrentLevel(null);
    setCurrentQuestions([]);
    setQuizResult(null);
    setCurrentPage('challenge');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return (
          <HomePage 
            user={user}
            progress={progress}
            chapters={chapters}
            levels={levels}
            achievements={achievements}
            onNavigate={setCurrentPage}
          />
        );
      case 'challenge':
        return (
          <ChallengePage
            levels={levels}
            chapters={chapters}
            progress={progress}
            onSelectLevel={handleSelectLevel}
          />
        );
      case 'quiz':
        return (
          <QuizPage
            level={currentLevel!}
            questions={currentQuestions}
            onComplete={handleQuizComplete}
            onExit={() => setCurrentPage('challenge')}
          />
        );
      case 'result':
        return (
          <ResultPage
            result={quizResult}
            level={currentLevel!}
            onRetry={handleRestart}
            onBack={() => setCurrentPage('home')}
          />
        );
      case 'profile':
        return (
          <ProfilePage
            user={user}
            progress={progress}
            achievements={achievements}
            levels={levels}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="app-container">
      {renderPage()}
      
      {currentPage !== 'quiz' && currentPage !== 'result' && (
        <div className="bottom-nav">
          <button 
            className={`nav-item ${currentPage === 'home' ? 'active' : ''}`}
            onClick={() => setCurrentPage('home')}
          >
            <span className="nav-icon">🏠</span>
            <span>首页</span>
          </button>
          <button 
            className={`nav-item ${currentPage === 'challenge' ? 'active' : ''}`}
            onClick={() => setCurrentPage('challenge')}
          >
            <span className="nav-icon">🎯</span>
            <span>闯关</span>
          </button>
          <button 
            className={`nav-item ${currentPage === 'profile' ? 'active' : ''}`}
            onClick={() => setCurrentPage('profile')}
          >
            <span className="nav-icon">👤</span>
            <span>我的</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default App;
